import { AgeRatedPlanData, CompositePlanDataResponse, QuoteDataForOpportunityOutput } from "../types/QuoteTypes"

export const getHTMLForQuoteDocumentGeneration = (quoteData: QuoteDataForOpportunityOutput, fontSize: number): string => {
    const fontSizePx = fontSize
    const opportunity = quoteData.Opportunity

    const quoteType = opportunity["cw_quotetype@OData.Community.Display.V1.FormattedValue"]

    const newPlans = quoteData.NewPlans?.PlanOptionsData?.PlanData || []
    const totalPlanCols = newPlans.length

    let totalTierCount = 0
    let tierData: { name: string; secCol: string | number; thirdCol: string | number }[] = []
    if (quoteType === "Composite") {
        tierData = ["EE", "ES", "E1", "EC", "EF"].map((code, i) => ({
            name: ["Employee", "Employee+Spouse", "Employee+1 Child", "Employee+Children", "Family"][i],
            secCol: code,
            thirdCol: quoteData.NewPlans?.PlanOptionsData?.[`${code}Count`] || 0
        }))

        if (opportunity["cw_censustype"] === 0) {
            tierData = tierData.filter(tier => tier.secCol !== "E1")
        }

        totalTierCount = tierData.reduce((sum, t) => sum + Number(t.thirdCol), 0)
    } else {
        tierData =
            newPlans[0] && (newPlans[0] as AgeRatedPlanData).HouseholdMemberData
                ? (newPlans[0] as AgeRatedPlanData).HouseholdMemberData.map(member => ({
                    name: member.Name,
                    secCol: member.Age,
                    thirdCol: member.Tier
                }))
                : []
    }

    //Reusable styles
    const border2 = "border: 1px solid #d3d0d0;"
    const borderR2 = "border-right: 1px solid #d3d0d0;"
    const textCenter = "text-align: center;"
    const bgColorPlan = "background-color:#ddebf7;"
    const padding = "padding-top: 3px; padding-bottom: 3px; padding-left: 5px; padding-right: 5px; white-space: nowrap;"

    const genPlanCells = (content: string) => {
        return newPlans.map((plan: any) => `<td style="${border2} ${bgColorPlan} ${textCenter} ${padding}"><strong>${plan[content] || content}</strong></td>`).join("")
    }

    const generatePlanHeaderRow = () => `<tr>${genPlanCells("Name")}</tr>`

    const generatePlanDetailRow = (label: string, isFirstRow: boolean, isLastLabel: boolean, isLastRow: boolean) => {
        const bLabel = isLastLabel ? "border-bottom: 1px solid #d3d0d0;" : "border-bottom: 1px solid #d3d0d0;"
        const bTop = isFirstRow ? "" : "border-top: 0;"
        const bBot = isLastRow ? "border-bottom: 1px solid #d3d0d0;" : "border-bottom: 0;"
        const cells = newPlans.map((plan: any) => `<td style="${borderR2} ${bTop} ${bBot} ${textCenter} ${bgColorPlan} ${padding}"><strong>${plan[label] || label}</strong></td>`).join("")
        return `<tr><td style="${borderR2} ${bLabel} ${textCenter} ${padding}"><strong>${label}:</strong></td>${cells}</tr>`
    }

    const formatToUSD = amount => {
        const number = parseFloat(String(amount).replace(/,/g, "").replace(/[$]/g, ""))
        if (isNaN(number)) {
            return "$0.00"
        }
        return number.toLocaleString("en-US", { style: "currency", currency: "USD" })
    }

    const generateTierRows = () =>
        tierData
            .map((tier, i) => {
                const bTop = i === 0 ? "border-top: 1px solid #d3d0d0;" : ""
                const bBot = i === tierData.length - 1 ? "border-bottom: 1px solid #d3d0d0;" : "border-bottom: 1px solid #d3d0d0;"
                if (quoteType === "Composite") {
                    return `<tr><td style="border-left: 1px solid #d3d0d0; ${bBot} ${padding} border-right: 1px solid #d3d0d0;"><strong>${tier.name}</strong></td><td style="${borderR2} ${bTop} ${bBot} ${textCenter}">${tier.secCol}</td><td style="${borderR2} ${bTop} ${bBot} ${textCenter}">${tier.thirdCol}</td>${newPlans.map((plan: any) => `<td style="${borderR2} ${bTop} ${bBot} ${textCenter}">${formatToUSD(plan[tier.secCol])}</td>`).join("")}</tr>`
                } else {
                    return `<tr><td style="border-left: 1px solid #d3d0d0; ${bBot} ${padding} border-right: 1px solid #d3d0d0;"><strong>${tier.name}</strong></td><td style="${borderR2} ${bTop} ${bBot} ${textCenter}">${tier.secCol}</td><td style="${borderR2} ${bTop} ${bBot} ${textCenter}">${tier.thirdCol}</td>${newPlans.map((plan: any) => `<td style="${borderR2} ${bTop} ${bBot} ${textCenter}">${formatToUSD(plan.HouseholdMemberData[i].Cost)}</td>`).join("")}</tr>`
                }
            })
            .join("")

    const generateTotalRow = () => {
        if (quoteType === "Composite") {
            let planTotal = newPlans.map((plan: any) => formatToUSD(plan.MonthlyTotal))
            if (opportunity["cw_censustype"] === 0) {
                const e1Count = (quoteData.NewPlans?.PlanOptionsData as CompositePlanDataResponse)?.E1Count || 0
                planTotal = newPlans.map((plan: any) => {
                    const monthlyTotal = parseFloat(String(plan.MonthlyTotal).replace(/,/g, "").replace(/[$]/g, "")) || 0
                    const e1Cost = parseFloat(String(plan.E1).replace(/,/g, "").replace(/[$]/g, "")) || 0
                    return formatToUSD(monthlyTotal - (e1Cost * e1Count))
                })
            }
            return `<tr><td colspan="2" style="${border2} ${textCenter} ${padding}"><strong>Monthly Total</strong></td><td style="${border2} ${textCenter}">${totalTierCount}</td>${planTotal.map(total => `<td style="${border2} ${textCenter}">${total}</td>`).join("")}</tr>`
        } else {
            return `<tr><td colspan="3" style="${border2} ${textCenter} ${padding}"><strong>Monthly Total</strong></td>${newPlans.map((plan: any) => `<td style="${border2} ${textCenter}">${formatToUSD(plan.MonthlyTotal)}</td>`).join("")}</tr>`
        }
    }

    const generateCheckboxRow = () =>
        `<tr><td style="border: none;"></td><td style="border: none;"></td><td style="border: none;"></td>${newPlans.map((plan: any) => `<td style="border: none; ${textCenter} padding-top: 20px; color: #ffffff; font-size: 1px;">{c*:primary:${plan.Name.replaceAll(" ", "")}}</td>`).join("")}</tr>`

    //Build HTML
    const html = `<table style="width: 700px; max-width: 700px; border-collapse: collapse; font-size: ${fontSizePx}px;">
    	<thead>
    	<tr>
    	<td rowspan="8" colspan="2" style="border: 1px solid #d3d0d0;"></td>
    	<td rowspan="2" style="border: 1px solid #d3d0d0;"></td>
    	<td colspan="${totalPlanCols}" style="${border2} ${textCenter} ${bgColorPlan} ${padding}"><strong>Plan Options</strong></td>
    	</tr>
    	${generatePlanHeaderRow()}
    	${generatePlanDetailRow("Deductible", true, false, false)}
    	${generatePlanDetailRow("Coinsurance", false, false, false)}
    	${generatePlanDetailRow("OOPMax", false, false, false)}
    	${generatePlanDetailRow("OfficeVisit", false, false, false)}
    	${generatePlanDetailRow("ERVisit", false, false, false)}
    	${generatePlanDetailRow("RxRetail", false, true, true)}
   		<tr>
    	${quoteType === "Age Rated"
            ? `<td style="border: 1px solid #d3d0d0; text-align: center;"><strong>Name</strong></td>
				<td style="border: 1px solid #d3d0d0; text-align: center;"><strong>Age</strong></td>
				<td style="border: 1px solid #d3d0d0; text-align: center;"><strong>Tier</strong></td>`
            : `<td colspan="2" style="border: 1px solid #d3d0d0; text-align: center;"><strong>Tier</strong></td>
    	<td style="border: 1px solid #d3d0d0; text-align: center;"><strong>Count</strong></td>`
        }
    	<td colspan="${totalPlanCols}" style="border-right: 1px solid #d3d0d0;"></td>
    	</tr>
    	${generateTierRows()}
    	${generateTotalRow()}
    	${generateCheckboxRow()}
    	</tbody>
    	</table>`

    return html
}